import { useState, useEffect, useCallback, useContext } from "react";
import Swal from "sweetalert2";
import InputMask from "react-input-mask";
import Joyride, { STATUS, EVENTS } from "react-joyride";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { useNavigate } from "react-router-dom";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { useMaterialUIController, AuthContext } from "context";
import { useUpgrade } from "context/upgrade";
import HttpService from "services/htttp.service";
import LeadService from "services/lead.service";
import { getApiErrorMessage, isHandledGlobally } from "utils/api-errors";

import LeadCard from "layouts/leads/components/LeadCard";
import LockedLeadCard from "layouts/leads/components/LockedLeadCard";
import useUnlockReturn from "layouts/leads/components/useUnlockReturn";
import { formatLeadDate as formatDate, isVisibleLead } from "layouts/leads/components/leadFormat";

const TAB_AVAILABLE = "available";
const TAB_MINE = "mine";

// Dummy Lead for Tour
const dummyLead = {
  id: 'tour-dummy',
  uuid: 'tour-dummy',
  locked: false,
  event_type: 'Aniversário (Exemplo)',
  event_date: new Date().toISOString(),
  client: { name: 'João Silva (Exemplo)' },
  location: 'Espaço de Eventos',
  guests_count: 80,
  message: 'Olá, gostaria de saber o valor para tocar no meu aniversário.',
  status: 'pending',
  phone: '11999999999'
};

const emptyManualForm = {
  name: "",
  email: "",
  phone: "",
  event_date: "",
  event_type: "aniversario",
  start_time: "",
  end_time: "",
  location: "",
  guests_count: "",
};

function Leads() {
  const [tab, setTab] = useState(TAB_AVAILABLE);
  const [availableLeads, setAvailableLeads] = useState([]);
  const [myLeads, setMyLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unlockingUuid, setUnlockingUuid] = useState(null);
  const [refreshNavbar, setRefreshNavbar] = useState(0);
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [conversionPrice, setConversionPrice] = useState("");

  // States for Joyride Tour
  const [runTour, setRunTour] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [tourSteps] = useState([
    {
      target: 'body',
      content: 'Aqui ficam os Pedidos de Orçamento. Em "Disponíveis" você libera o contato de novos pedidos; em "Meus pedidos" você negocia os que já liberou.',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '#btn-create-lead',
      content: 'Você pode criar um pedido manualmente se o cliente entrar em contato por fora da plataforma.',
    },
    {
      target: '#lead-card-0',
      content: 'Este é um cartão de pedido. Ele mostra o tipo, a data e o local do evento.',
    },
    {
      target: '#lead-status-0',
      content: 'Acompanhe a situação do pedido aqui.',
    },
    {
      target: '#lead-actions-0',
      content: 'Use estes botões para liberar o contato ou, depois de liberado, Aceitar ou Recusar o pedido.',
    }
  ]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // New states for manual lead creation
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [manualForm, setManualForm] = useState(emptyManualForm);

  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const { sidenavColor, darkMode } = controller;
  const { user, setUser, getUserData } = useContext(AuthContext);
  const { openUpgrade } = useUpgrade();

  const isPremium = Boolean(user?.is_premium);
  // Vitalício: unlocks_unlimited = true e cota/restantes vêm null
  const unlimitedUnlocks = Boolean(user?.unlocks_unlimited);
  const unlocksRemaining = user?.unlocks_remaining || 0;

  useEffect(() => {
    // Inicia o tour se ainda não foi visto
    if (user && !user.tours?.leads) {
      setRunTour(true);
    }
  }, [user]);

  const handleJoyrideCallback = (data) => {
    const { status, type, index, action } = data;

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      setStepIndex(index + (action === "prev" ? -1 : 1));
    } else if (type === EVENTS.TOUR_START) {
      setStepIndex(0);
    }

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      setStepIndex(0);
      HttpService.post("my-tours/leads");
      if (setUser) {
        setUser((prev) => ({
          ...prev,
          tours: { ...prev?.tours, leads: 1 },
        }));
      }
    }
  };

  const fetchLeads = useCallback(async () => {
    // Uma lista falhar não derruba a outra
    const [available, mine] = await Promise.allSettled([LeadService.getAvailable(), LeadService.getMine()]);
    if (available.status === "fulfilled") setAvailableLeads(available.value);
    else console.error("Erro ao buscar pedidos disponíveis:", available.reason);
    if (mine.status === "fulfilled") setMyLeads(mine.value);
    else console.error("Erro ao buscar meus pedidos:", mine.reason);
    setLoading(false);
  }, []);

  // Depois de liberar ou mudar status: listas, cota (/me) e badge do navbar
  const refreshAll = useCallback(async () => {
    await Promise.all([fetchLeads(), getUserData ? getUserData() : null]);
    setRefreshNavbar((prev) => prev + 1);
  }, [fetchLeads, getUserData]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useUnlockReturn({
    onConfirmed: async () => {
      setTab(TAB_MINE);
      await refreshAll();
    },
    onRetry: async () => {
      setTab(TAB_AVAILABLE);
      await refreshAll();
    },
  });

  const handleUnlock = async (lead, unlockState) => {
    if (unlockingUuid) return;

    if (unlockState.useQuota) {
      const confirm = await Swal.fire({
        icon: "question",
        title: "Liberar contato?",
        text: `Você vai usar 1 das suas liberações do mês. Restam ${unlocksRemaining}.`,
        showCancelButton: true,
        confirmButtonText: "Liberar",
        cancelButtonText: "Cancelar",
      });
      if (!confirm.isConfirmed) return;
    }

    setUnlockingUuid(lead.uuid);
    try {
      const res = await LeadService.unlock(lead.uuid);

      if (res?.status === "checkout" && res.url) {
        // Stripe (cartão, ou Pix se pix_enabled). A volta cai em /dashboard/pedidos?unlock=...
        window.location.href = res.url;
        return;
      }

      if (res?.status === "unlocked") {
        setTab(TAB_MINE);
        await refreshAll();
        Swal.fire({
          icon: "success",
          title: "Contato liberado!",
          text:
            typeof res.unlocks_remaining === "number"
              ? `Restam ${res.unlocks_remaining} liberações este mês.`
              : "O pedido está em Meus pedidos.",
          timer: 2500,
          showConfirmButton: false,
        });
      }
      setUnlockingUuid(null);
    } catch (error) {
      setUnlockingUuid(null);
      console.error("Erro ao liberar pedido:", error);
      if (isHandledGlobally(error)) return;

      // Situação do pedido mudou (esgotou, expirou, sumiu): lista atualizada
      const listChanged = ["lead_full", "lead_expired"].includes(error?.code) || error?.httpStatus === 404;
      if (listChanged) fetchLeads();

      if (error?.code === "unlock_pending") {
        Swal.fire("Pagamento em andamento", getApiErrorMessage(error), "info");
      } else if (error?.httpStatus === 404) {
        Swal.fire("Pedido indisponível", "Este pedido não está mais disponível.", "warning");
      } else {
        Swal.fire("Não foi possível liberar", getApiErrorMessage(error, "Tente novamente em instantes."), "warning");
      }
    }
  };

  const handleAccept = async (lead) => {
    try {
      await LeadService.accept(lead.uuid);

      // Recarrega a lista do servidor para garantir que o status foi salvo
      await fetchLeads();
      setRefreshNavbar((prev) => prev + 1);

      Swal.fire({
        icon: "success",
        title: "Pedido Aceito!",
        text: "Agora você pode entrar em contato com o cliente.",
        timer: 2000,
        showConfirmButton: false
      });

    } catch (error) {
      console.error("Erro ao aceitar pedido:", error);
      if (isHandledGlobally(error)) return;
      Swal.fire("Erro", getApiErrorMessage(error, "Não foi possível aceitar o pedido."), "error");
    }
  };

  const handleReject = async (lead) => {
    const result = await Swal.fire({
      title: 'Tem certeza?',
      text: "Você não poderá recuperar este pedido depois.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, recusar',
      cancelButtonText: 'Cancelar'
    });
    if (!result.isConfirmed) return;

    try {
      await LeadService.reject(lead.uuid);

      // Recarrega a lista para confirmar a remoção
      await fetchLeads();
      setRefreshNavbar((prev) => prev + 1);

      Swal.fire('Recusado!', 'O pedido foi removido da sua lista.', 'success');
    } catch (error) {
      console.error("Erro ao recusar pedido:", error);
      if (isHandledGlobally(error)) return;
      if (error?.httpStatus === 404) {
        fetchLeads();
        Swal.fire("Pedido indisponível", "Este pedido não está mais disponível.", "warning");
        return;
      }
      Swal.fire("Erro", getApiErrorMessage(error, "Não foi possível recusar o pedido."), "error");
    }
  };

  // Converte o pedido em evento; contrato em /contracts/event/:eventId (Premium)
  const handleOpenContract = (lead) => {
    navigate(`/contracts/event/${lead.event_id}`);
  };

  const handleOpenConvert = (lead) => {
    if (lead.id === 'tour-dummy') {
      Swal.fire("Modo Tutorial", "No modo real, isso abriria a tela para confirmar valores e gerar contrato.", "info");
      return; 
    }
    setSelectedLead(lead);
    setConversionPrice("");
    setConvertModalOpen(true);
  };

  const handleConvertLead = async () => {
    if (!selectedLead) return;

    if (!conversionPrice) {
        Swal.fire("Atenção", "Por favor, informe o valor fechado.", "warning");
        return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      await LeadService.convert(selectedLead.uuid, {
        price: conversionPrice,
        status: 'converted'
      });

      setConvertModalOpen(false);
      await fetchLeads();
      setRefreshNavbar((prev) => prev + 1);

      Swal.fire({
        icon: "success",
        title: "Convertido!",
        text: "O lead foi transformado em evento e o contrato foi gerado.",
        timer: 2500,
        showConfirmButton: false
      });

    } catch (error) {
      console.error("Erro ao converter lead:", error);

      if (isHandledGlobally(error)) {
        setConvertModalOpen(false);
        return;
      }

      const isGoogleError =
        error?.exception?.includes("Google") ||
        error?.message?.includes("invalid_grant") ||
        error?.message?.includes("Google");

      if (isGoogleError) {
        setConvertModalOpen(false);
        await fetchLeads();
        setRefreshNavbar((prev) => prev + 1);
        Swal.fire({
          icon: "warning",
          title: "Evento criado!",
          text: "O evento foi salvo, mas não foi possível sincronizar com o Google Calendar pois o token de acesso expirou. Reconecte sua conta Google nas configurações.",
          confirmButtonText: "Entendido",
        });
      } else {
        Swal.fire("Erro", "Não foi possível converter o lead.", "error");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteLead = async (lead) => {
    const result = await Swal.fire({
      title: 'Excluir pedido?',
      text: 'O pedido será removido da sua lista.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
    });

    if (!result.isConfirmed) return;

    try {
      await LeadService.remove(lead.uuid);
      await fetchLeads();
      setRefreshNavbar((prev) => prev + 1);
      Swal.fire('Excluído!', 'O pedido foi removido da sua lista.', 'success');
    } catch (error) {
      console.error('Erro ao excluir pedido:', error);
      if (isHandledGlobally(error)) return;
      Swal.fire('Erro', 'Não foi possível excluir o pedido.', 'error');
    }
  };

  const handleOpenCreate = () => {
    if (!isPremium) {
      openUpgrade();
      return;
    }
    setCreateModalOpen(true);
  };

  const handleManualFormChange = (e) => {
    setManualForm({ ...manualForm, [e.target.name]: e.target.value });
  };

  const handleCreateManualLead = async () => {
    if (!manualForm.name || !manualForm.email || !manualForm.phone || !manualForm.event_date) {
      Swal.fire("Atenção", "Preencha os campos obrigatórios: Nome, Email, Telefone e Data.", "warning");
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // Sanitização do Payload para evitar erros 500 no Backend
      const payload = {
        ...manualForm,
        phone: manualForm.phone.replace(/\D/g, ""), // Remove parênteses e traços, envia apenas números
        guests_count: manualForm.guests_count ? parseInt(manualForm.guests_count, 10) : null, // Garante Integer ou Null
      };

      await LeadService.createManual(payload);
      setCreateModalOpen(false);
      setManualForm(emptyManualForm);
      setTab(TAB_MINE);
      await fetchLeads();
      Swal.fire("Sucesso", "Pedido criado com sucesso!", "success");
    } catch (error) {
      console.error("Erro ao criar pedido manual:", error);
      if (isHandledGlobally(error)) {
        setCreateModalOpen(false);
        return;
      }
      Swal.fire("Erro", "Não foi possível criar o pedido.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const visibleAvailable = availableLeads.filter(isVisibleLead);
  const visibleMine = myLeads.filter(isVisibleLead);
  const currentLeads = tab === TAB_AVAILABLE ? visibleAvailable : visibleMine;

  // Lógica para exibir o Dummy Lead durante o tour se a lista estiver vazia
  const leadsToRender = (runTour && currentLeads.length === 0) ? [dummyLead] : currentLeads;

  const emptyMessage = tab === TAB_AVAILABLE
    ? "Nenhum pedido disponível no momento. Novos pedidos aparecem aqui assim que os clientes pedirem orçamento."
    : "Você ainda não tem pedidos. Libere um pedido em \"Disponíveis\" para ver o contato do cliente.";

  return (
    <DashboardLayout>
      <Joyride
        steps={tourSteps}
        run={runTour}
        stepIndex={stepIndex}
        continuous
        showSkipButton
        showProgress={false}
        callback={handleJoyrideCallback}
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: sidenavColor === 'default' ? '#1A73E8' : 'var(--mui-palette-' + sidenavColor + '-main, #1A73E8)',
          },
        }}
        locale={{
          back: 'Voltar',
          close: 'Fechar',
          last: 'Finalizar',
          next: `Próximo (${stepIndex + 1} de ${tourSteps.length})`,
          skip: 'Pular'
        }}
      />
      <DashboardNavbar refreshTrigger={refreshNavbar} />
      <MDBox pt={6} pb={3}>
        <MDBox mb={3} display="flex" justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} flexDirection={{ xs: "column", sm: "row" }} gap={2}>
          <MDBox>
            <MDTypography variant="h4" fontWeight="medium">
              Pedidos de Orçamento
            </MDTypography>
            <MDTypography variant="button" color="text">
              Libere o contato dos pedidos e negocie direto com o cliente.
            </MDTypography>
          </MDBox>
          <MDButton id="btn-create-lead" variant="gradient" color={sidenavColor} onClick={handleOpenCreate}>
            {!isPremium && <Icon sx={{ mr: 0.5 }}>lock</Icon>}
            Criar Pedido
          </MDButton>
        </MDBox>

        {/* Cota de liberações */}
        {user && (
          <Card sx={{ mb: 3 }}>
            <MDBox p={2} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <MDBox display="flex" alignItems="center" gap={1}>
                <Icon color="info">lock_open</Icon>
                {unlimitedUnlocks ? (
                  <MDTypography variant="button" color="text">
                    Plano vitalício: liberações <strong>ilimitadas</strong>, inclusive em pedidos com as vagas esgotadas.
                  </MDTypography>
                ) : isPremium ? (
                  <MDTypography variant="button" color="text">
                    Liberações do mês: restam <strong>{unlocksRemaining}</strong> de {user.unlocks_quota || 0}.
                    {unlocksRemaining === 0 && " Você ainda pode liberar pedidos avulsos."}
                  </MDTypography>
                ) : (
                  <MDTypography variant="button" color="text">
                    Plano Free: libere pedidos avulsos, sem assinar. O Premium inclui 10 liberações por mês.
                  </MDTypography>
                )}
              </MDBox>
              {!isPremium && (
                <MDButton variant="text" color="info" size="small" onClick={openUpgrade}>
                  Ver planos
                </MDButton>
              )}
            </MDBox>
          </Card>
        )}

        <MDBox mb={3}>
          <Tabs value={tab} onChange={(e, value) => setTab(value)}>
            <Tab value={TAB_AVAILABLE} label={`Disponíveis (${visibleAvailable.length})`} />
            <Tab value={TAB_MINE} label={`Meus pedidos (${visibleMine.length})`} />
          </Tabs>
        </MDBox>

        <Grid container spacing={3}>
          {loading ? (
             <Grid item xs={12}>
                <MDBox display="flex" justifyContent="center">
                  <MDTypography>Carregando pedidos...</MDTypography>
                </MDBox>
             </Grid>
          ) : leadsToRender.length === 0 ? (
             <Grid item xs={12}>
                <Card>
                  <MDBox p={3} textAlign="center">
                    <MDTypography variant="h6" color="text">
                      {emptyMessage}
                    </MDTypography>
                  </MDBox>
                </Card>
             </Grid>
          ) : (
            leadsToRender.map((lead, index) => (
              <Grid item xs={12} md={6} lg={4} key={lead.uuid || lead.id} className="lead-grid-item">
                {lead.locked ? (
                  <LockedLeadCard
                    lead={lead}
                    index={index}
                    unlocksRemaining={unlocksRemaining}
                    unlimited={unlimitedUnlocks}
                    unlocking={unlockingUuid === lead.uuid}
                    onUnlock={handleUnlock}
                  />
                ) : (
                  <LeadCard
                    lead={lead}
                    index={index}
                    isPremium={isPremium}
                    isDummy={lead.id === 'tour-dummy'}
                    onAccept={handleAccept}
                    onReject={handleReject}
                    onDelete={handleDeleteLead}
                    onConvert={handleOpenConvert}
                    onOpenContract={handleOpenContract}
                    onUpgrade={openUpgrade}
                  />
                )}
              </Grid>
            ))
          )}
        </Grid>
      </MDBox>

      {/* Modal Criar Pedido Manual */}
      <Dialog open={createModalOpen} onClose={() => setCreateModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Criar Pedido</DialogTitle>
        <DialogContent>
          <MDBox pt={2} display="flex" flexDirection="column" gap={2}>
            <MDInput label="Nome *" name="name" value={manualForm.name} onChange={handleManualFormChange} fullWidth />
            <MDInput label="Email *" name="email" type="email" value={manualForm.email} onChange={handleManualFormChange} fullWidth />
            <InputMask
              mask="(99) 99999-9999"
              value={manualForm.phone}
              onChange={handleManualFormChange}
              disabled={false}
            >
              {(inputProps) => (
                <MDInput {...inputProps} label="Telefone *" name="phone" fullWidth />
              )}
            </InputMask>
            
            <MDInput 
              label="Data do Evento *" 
              name="event_date" 
              type="date" 
              value={manualForm.event_date} 
              onChange={handleManualFormChange} 
              fullWidth 
              InputLabelProps={{ shrink: true }} 
            />

            <MDInput 
              select 
              label="Tipo de Evento" 
              name="event_type" 
              value={manualForm.event_type} 
              onChange={handleManualFormChange} 
              fullWidth 
              SelectProps={{ native: true }}
            >
              <option value="aniversario" style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }}>Aniversário</option>
              <option value="casamento" style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }}>Casamento</option>
              <option value="debutante" style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }}>Debutante</option>
              <option value="formatura" style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }}>Formatura</option>
              <option value="corporativo" style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }}>Corporativo</option>
              <option value="outros" style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }}>Outros</option>
            </MDInput>

            <MDBox display="flex" gap={2}>
              <MDInput label="Início" name="start_time" type="time" value={manualForm.start_time} onChange={handleManualFormChange} fullWidth InputLabelProps={{ shrink: true }} />
              <MDInput label="Fim" name="end_time" type="time" value={manualForm.end_time} onChange={handleManualFormChange} fullWidth InputLabelProps={{ shrink: true }} />
            </MDBox>

            <MDInput label="Local" name="location" value={manualForm.location} onChange={handleManualFormChange} fullWidth />
            <MDInput label="Nº Convidados" name="guests_count" type="number" value={manualForm.guests_count} onChange={handleManualFormChange} fullWidth />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setCreateModalOpen(false)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={handleCreateManualLead} variant="gradient" color={sidenavColor} disabled={isSubmitting}>
            {isSubmitting ? "Salvando..." : "Salvar"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={convertModalOpen} onClose={() => setConvertModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Confirmar Contratação</DialogTitle>
        <DialogContent>
          <MDBox pt={1}>
            <MDTypography variant="body2" color="text" mb={2}>
              Ao confirmar, este pedido será convertido em um <strong>Evento</strong> na sua agenda e um <strong>Contrato</strong> será gerado automaticamente.
            </MDTypography>
            
            {selectedLead && (
              <MDBox p={2} bgColor="#f0f2f5" borderRadius="lg" mb={3}>
                <MDTypography variant="button" fontWeight="bold" display="block" gutterBottom>
                  Cliente: <span style={{ fontWeight: "normal" }}>{selectedLead.client?.name || selectedLead.name || "Não informado"}</span>
                </MDTypography>
                <MDTypography variant="button" fontWeight="bold" display="block" gutterBottom>
                  Data do Evento: <span style={{ fontWeight: "normal" }}>{formatDate(selectedLead.event_date || selectedLead.eventDate)}</span>
                </MDTypography>
                <MDTypography variant="button" fontWeight="bold" display="block">
                  Tipo: <span style={{ fontWeight: "normal" }}>{selectedLead.event_type || selectedLead.eventType}</span>
                </MDTypography>
              </MDBox>
            )}

            <MDInput
              label="Valor Fechado (R$)"
              type="number"
              fullWidth
              value={conversionPrice}
              onChange={(e) => setConversionPrice(e.target.value)}
              placeholder="Ex: 1500.00"
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setConvertModalOpen(false)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={handleConvertLead} variant="gradient" color="success" disabled={isSubmitting}>
            {isSubmitting ? "Processando..." : "Converter em Evento"}
          </MDButton>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default Leads;
